import React, { useEffect, useState, useMemo } from "react";
import { Box, Flex, Heading, Spinner, Text, useColorModeValue, Progress } from "@chakra-ui/react";
import useAxios from "../utils/useAxios";

interface LinkQuality {
  tot_hops: number;
  tot_rssi: number;
  device_address: string; // The parent's address
  created_at: string;
}

interface DeviceHealth {
  id: number;
  device_address: string;
  user_device_name: string | null;
  link_quality: LinkQuality | null;
  battery_level: number;
}

interface TreeNode extends DeviceHealth {
  children: TreeNode[];
  x: number;
  y: number;
  width: number;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 130;
const HORIZONTAL_SPACING = 30;
const VERTICAL_SPACING = 150;

const DeviceNode = ({ node }: { node: TreeNode }) => {
  const isGateway = node.device_address === "000000000000";
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const batteryPercent = Math.min(
    100,
    Math.max(0, ((node.battery_level - 2000) / (3000 - 2000)) * 100),
  );
  let batteryColor = "green";
  if (batteryPercent < 20) batteryColor = "red";
  else if (batteryPercent < 50) batteryColor = "yellow";

  return (
    <foreignObject x={node.x} y={node.y} width={NODE_WIDTH} height={NODE_HEIGHT}>
      <Box
        w="100%"
        h="100%"
        p={2}
        bg={bgColor}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        boxShadow="sm"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        fontSize="xs"
        overflow="hidden"
      >
        <Box>
          <Text
            fontWeight="bold"
            noOfLines={1}
            title={node.user_device_name || node.device_address}
          >
            {node.user_device_name || node.device_address}
          </Text>
          <Text fontSize="2xs" color="gray.500" mb={1}>
            {node.device_address}
          </Text>
          {!isGateway && (
            <>
              {node.link_quality ? (
                <>
                  <Text>RSSI: {node.link_quality.tot_rssi} dBm</Text>
                  <Text>Hops: {node.link_quality.tot_hops}</Text>
                </>
              ) : (
                <Text color="red.500">No Link Quality</Text>
              )}
            </>
          )}
        </Box>
        {!isGateway && (
          <Box mt={1}>
            <Flex justify="space-between" mb={1}>
              <Text fontSize="2xs">Battery</Text>
              <Text fontSize="2xs">{node.battery_level}mV</Text>
            </Flex>
            <Progress
              value={batteryPercent}
              size="sm"
              colorScheme={batteryColor}
              borderRadius="md"
            />
          </Box>
        )}
      </Box>
    </foreignObject>
  );
};

const DeviceTreeVisualization: React.FC<{ gatewayId: string }> = ({ gatewayId }) => {
  const api = useAxios();
  const [devices, setDevices] = useState<DeviceHealth[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gatewayId) return;

    setLoading(true);
    api
      .get(`monitoring/gateway-health/${gatewayId}/`)
      .then((res) => {
        setDevices(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch device health", err);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId]);

  const { treeNodes, edges, unconnectedNodes, svgWidth, svgHeight } = useMemo(() => {
    if (!devices.length)
      return { treeNodes: [], edges: [], unconnectedNodes: [], svgWidth: 0, svgHeight: 0 };

    const connectedDevices = devices.filter((d) => d.link_quality !== null);
    const unconnected = devices.filter((d) => d.link_quality === null);

    // Map device address to node
    const deviceMap = new Map<string, TreeNode>();
    connectedDevices.forEach((d) => {
      deviceMap.set(d.device_address, { ...d, children: [], x: 0, y: 0, width: NODE_WIDTH });
    });

    // The root is the gateway - but it's not in the list usually. The list contains devices connected to it.
    // We can synthesize a root node for the gateway.
    const rootNode: TreeNode = {
      id: 0,
      device_address: "000000000000", // Common address for coordinator
      user_device_name: "Gateway",
      link_quality: { tot_hops: 0, tot_rssi: 0, device_address: "", created_at: "" },
      battery_level: 0,
      children: [],
      x: 0,
      y: 0,
      width: NODE_WIDTH,
    };

    const allNodes: TreeNode[] = [rootNode]; // List of nodes to render in the tree structure

    // Build tree
    connectedDevices.forEach((d) => {
      const node = deviceMap.get(d.device_address)!;
      const parentAddr = d.link_quality?.device_address;

      if (parentAddr === "000000000000" || parentAddr === rootNode.device_address) {
        rootNode.children.push(node);
        allNodes.push(node);
      } else if (parentAddr && deviceMap.has(parentAddr)) {
        deviceMap.get(parentAddr)!.children.push(node);
        allNodes.push(node);
      } else {
        // Orphaned or parent not in list (maybe connected to another unknown node?)
        // Treat as child of root for visualization but maybe mark it?
        // Or just add to root if we can't find parent.
        // Assuming well-formed tree for now or fallback to root.
        rootNode.children.push(node);
        allNodes.push(node);
      }
    });

    // Layout algorithm
    // Simple Reingold-Tilford-ish or just level-based spacing
    // We can do a post-order traversal to calculate widths, then pre-order to set X?
    // Actually since we have fixed node widths, let's keep it simple.

    let currentX = 0;

    // Traverse to assign y (depth) and gather nodes at each depth
    const calculateLayout = (node: TreeNode, depth: number) => {
      node.y = depth * VERTICAL_SPACING + 20; // 20 padding top

      if (node.children.length === 0) {
        node.x = currentX;
        currentX += NODE_WIDTH + HORIZONTAL_SPACING;
      } else {
        node.children.forEach((child) => calculateLayout(child, depth + 1));
        // Center parent over children
        const firstChild = node.children[0];
        const lastChild = node.children[node.children.length - 1];
        node.x = (firstChild.x + lastChild.x) / 2;
      }
    };

    // Reset X for calculation
    currentX = 0;
    calculateLayout(rootNode, 0);

    // Shift all nodes if root x < 0 (not possible with this algo as we start at 0 and move right)
    // But we might want some padding on left
    const minX = Math.min(...allNodes.map((n) => n.x));
    if (minX < 20) {
      const shift = 20 - minX;
      allNodes.forEach((n) => (n.x += shift));
    }

    const maxX = Math.max(...allNodes.map((n) => n.x + NODE_WIDTH));
    const maxY = Math.max(...allNodes.map((n) => n.y + NODE_HEIGHT));

    const edgesToRender: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    allNodes.forEach((node) => {
      node.children.forEach((child) => {
        edgesToRender.push({
          x1: node.x + NODE_WIDTH / 2,
          y1: node.y + NODE_HEIGHT,
          x2: child.x + NODE_WIDTH / 2,
          y2: child.y,
        });
      });
    });

    // Make the SVG wide enough to fit all nodes, plus some margin
    const dynamicWidth = Math.max(maxX + 20, 800, maxX + HORIZONTAL_SPACING);

    return {
      treeNodes: allNodes,
      edges: edgesToRender,
      unconnectedNodes: unconnected,
      svgWidth: dynamicWidth,
      svgHeight: maxY + 20,
    };
  }, [devices]);

  const cardBg = useColorModeValue("white", "gray.900");
  const unconnectedBg = useColorModeValue("gray.50", "gray.800");

  if (loading)
    return (
      <Flex justify="center" p={10}>
        <Spinner />
      </Flex>
    );

  return (
    <Box mt={8} bg={cardBg} p={6} rounded="md" boxShadow="md" overflowX="auto" w="full">
      <Heading size="md" mb={6}>
        Device Network Topology
      </Heading>

      {/* Tree Visualization */}
      <Box
        overflowX="auto"
        border="1px dashed"
        borderColor="gray.200"
        borderRadius="md"
        mb={6}
        p={4}
      >
        <svg width={svgWidth} height={svgHeight}>
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="10"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#CBD5E0" />
            </marker>
          </defs>

          {/* Edges */}
          {edges.map((edge, i) => (
            <line
              key={`edge-${i}`}
              x1={edge.x1}
              y1={edge.y1}
              x2={edge.x2}
              y2={edge.y2}
              stroke="#CBD5E0"
              strokeWidth="2"
            />
          ))}

          {/* Nodes */}
          {treeNodes.map((node) => (
            <DeviceNode key={node.device_address} node={node} />
          ))}
        </svg>
      </Box>

      {/* Unconnected Devices */}
      {unconnectedNodes.length > 0 && (
        <Box mt={8} p={4} bg={unconnectedBg} borderRadius="md">
          <Heading size="sm" mb={4}>
            Devices without Link Quality
          </Heading>
          <Flex wrap="wrap" gap={4}>
            {unconnectedNodes.map((node) => (
              <Box
                key={node.id}
                w={`${NODE_WIDTH}px`}
                p={2}
                bg={cardBg}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="sm"
                fontSize="xs"
              >
                <Text fontWeight="bold" noOfLines={1}>
                  {node.user_device_name || node.device_address}
                </Text>
                <Text fontSize="2xs" color="gray.500" mb={1}>
                  {node.device_address}
                </Text>
                <Text color="gray.500">No Link Data</Text>
                <Text>Battery: {node.battery_level}mV</Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default DeviceTreeVisualization;
